<script setup lang="ts">
import { parseISO } from 'date-fns';
import { computed, ref, watch } from 'vue';
import { AppCoreEnums } from '../../shared/enums';
import { dateUtils } from '../../utils/date-utils';
import useAppI18n from '@/core/composable/use-app-i18n';
import { IDateRange } from '../../../stores/core/models';
import { dynamicFiltersUtils } from '../../utils/dynamic-filters-utils';
import { radiobuttonPreset } from '../../../presets/customizations/radiobutton';

const modelValue = defineModel<IDateRange | null>();

const props = defineProps({
  showTime: {
    type: Boolean,
    default: true,
  },
});

const { t } = useAppI18n();
const fromDateTime = ref(null as Date | null);
const toDateTime = ref(null as Date | null);
const rangeSelectedOption = ref(AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last2Days);

const dateRangeOptions = [
  {
    label: t('Last2Days'),
    value: AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last2Days,
  },
  {
    label: t('Last7Days'),
    value: AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last7Days,
  },
  {
    label: t('Last15Days'),
    value: AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last15Days,
  },
];

const dateRangeOptions2 = [
  {
    label: t('Today'),
    value: AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Today,
  },
  {
    label: t('Yesterday'),
    value: AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Yesterday,
  },
  {
    label: `${t('Custom')}:`,
    value: AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Custom,
  },
];

const dateRange = computed(() => {
  if (fromDateTime.value || toDateTime.value) {
    return {
      from: fromDateTime.value?.toISOString(),
      to: toDateTime.value?.toISOString(),
      rangeOption: rangeSelectedOption.value,
      rangeOptionLabelResourceId: dynamicFiltersUtils.getResourceIdBasedOnRangeOption(
        rangeSelectedOption.value
      ),
    } as IDateRange;
  } else {
    return null;
  }
});

watch(
  () => rangeSelectedOption.value,
  () => initRangeDatesBasedOnRangeOption()
);

watch(
  () => dateRange.value,
  () => (modelValue.value = dateRange.value)
);

const initRangeDatesBasedOnRangeOption = () => {
  const rangeDates = dynamicFiltersUtils.setDatesBasedOnRangeOption(
    rangeSelectedOption.value,
    modelValue.value,
    props.showTime
  );
  fromDateTime.value = parseISO(rangeDates.from as string);
  toDateTime.value = parseISO(rangeDates.to as string);
};

const init = () => {
  if (modelValue.value) {
    fromDateTime.value = modelValue.value.from ? parseISO(modelValue.value.from) : null;
    toDateTime.value = modelValue.value.to ? parseISO(modelValue.value.to) : null;
    rangeSelectedOption.value = modelValue.value.rangeOption;
  } else {
    fromDateTime.value = null;
    toDateTime.value = null;
    rangeSelectedOption.value = AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Last2Days;
  }

  initRangeDatesBasedOnRangeOption();
};

init();
</script>

<template>
  <div class="flex flex-row mt-5 d-block">
    <div class="flex flex-col gap-3 w-2/4">
      <div
        v-for="rangeOpt in dateRangeOptions2"
        :key="rangeOpt.value"
        class="flex align-items-center"
      >
        <RadioButton
          v-model="rangeSelectedOption"
          :inputId="rangeOpt.label"
          name="dynamic"
          :value="rangeOpt.value"
          :ptOptions="{ mergeSections: true, mergeProps: true }"
          :pt="radiobuttonPreset"
        />
        <label :for="rangeOpt.label" class="ml-2 cursor-pointer">{{ rangeOpt.label }}</label>
      </div>
    </div>
    <div class="flex flex-col gap-3 w-2/4">
      <div
        v-for="rangeOpt in dateRangeOptions"
        :key="rangeOpt.value"
        class="flex align-items-center"
      >
        <RadioButton
          v-model="rangeSelectedOption"
          :inputId="rangeOpt.label"
          name="dynamic"
          :value="rangeOpt.value"
          :ptOptions="{ mergeSections: true, mergeProps: true }"
          :pt="radiobuttonPreset"
        />
        <label :for="rangeOpt.label" class="ml-2 cursor-pointer">{{ rangeOpt.label }}</label>
      </div>
    </div>
  </div>

  <div class="flex flex-row mt-5">
    <!-----------
    -- From Date --
    ------------->
    <div class="flex w-2/4 mr-5">
      <div class="flex flex-col">
        <label for="fromDate" class="font-semibold text-surface-500 dark:text-white/70 mb-2">
          From
        </label>
        <DatePicker
          v-model="fromDateTime"
          showIcon
          :max="toDateTime"
          inputId="fromDate"
          iconDisplay="input"
          :showTime="props.showTime"
          hourFormat="24"
          input-class="!w-[175px]"
          :dateFormat="dateUtils.getPrimeDateFormat()"
          :disabled="rangeSelectedOption !== AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Custom"
        />
      </div>
    </div>

    <!-----------
    -- To Date --
    ------------->
    <div class="flex w-2/4">
      <div class="flex flex-col">
        <label for="toDate" class="font-semibold text-surface-500 dark:text-white/70 mb-2">
          To
        </label>
        <DatePicker
          v-model="toDateTime"
          showIcon
          inputId="toDate"
          iconDisplay="input"
          :showTime="props.showTime"
          hourFormat="24"
          input-class="!w-[175px]"
          :min="fromDateTime"
          :dateFormat="dateUtils.getPrimeDateFormat()"
          :disabled="rangeSelectedOption !== AppCoreEnums.DynamicFilterDateRangeOptionsEnum.Custom"
        />
      </div>
    </div>
  </div>
</template>
